import { removeLeadingZeros } from 'utils/functions';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { SEAT_SELECTION_INVENTORY_CLASSES } from 'constants/inventoryClass';

const getSeatsFromLegs = (legs) => legs.flatMap(({ seats }) => seats);

const flattenSeats = ({ outbound, inbound }) =>
  [...getSeatsFromLegs(outbound), ...getSeatsFromLegs(inbound)].map(({ number, itemRef, coach: carriage }) => ({
    number: removeLeadingZeros(number),
    itemRef,
    carriage
  }));

const findSeatsToUpdate = (originalSeats, newSeats) => {
  const flattenedOriginalSeats = flattenSeats(originalSeats);
  const flattenedNewSeats = flattenSeats(newSeats);

  return flattenedNewSeats.filter((newSeat) => {
    const originalSeat = flattenedOriginalSeats.find((seat) => seat.itemRef === newSeat.itemRef);

    return originalSeat.carriage !== newSeat.carriage || originalSeat.number !== newSeat.number;
  });
};

const getLegWithSeatingData = (leg, isNewBooking) => ({
  ...leg,
  seats: leg.seats
    .filter((seat) => {
      const product = leg.products?.find((p) => String(p.itemRef) === String(seat.itemRef));
      return !(product?.cancelled && product?.provisional);
    })
    .map((seat) => {
      if (isNewBooking) {
        return { ...seat, editable: true };
      }

      const bookingItem = leg.bookingItems.find((item) => !item.cancelled && item.passenger.id === seat.passenger.id);

      return {
        ...seat,
        editable: Boolean(bookingItem && bookingItem.isSeatEditable)
      };
    })
});

const pickSeats = (
  { outbound, inbound },
  { isNewBooking, isMissedDeparture, isFlowForwardCapped, nextAvailableTrain, selectedPassengerIds }
) => {
  const seats = {
    outbound: outbound.map((leg) => getLegWithSeatingData(leg, isNewBooking)),
    inbound: inbound.map((leg) => getLegWithSeatingData(leg, isNewBooking))
  };

  if (isMissedDeparture || isFlowForwardCapped) {
    // set recommended seats by default
    const recommendedSeats = nextAvailableTrain?.recommendedSeats || [];
    const legs = seats.outbound.length > 0 ? seats.outbound : seats.inbound;
    const esLeg = legs.find((leg) => leg.serviceType.code === serviceTypeCodes.EUROSTAR);

    esLeg.seats = esLeg.seats.filter((seat) => selectedPassengerIds.includes(seat.passenger.id));

    for (let i = 0; i < recommendedSeats.length; i += 1) {
      esLeg.seats[i].coach = recommendedSeats[i]?.coach;
      esLeg.seats[i].number = recommendedSeats[i]?.number;
    }
  }

  return seats;
};

const findSelectedLeg = (legId, { outbound = [], inbound = [] } = {}) => [...outbound, ...inbound].find(({ id }) => id === legId);

const findAllServiceSegments = (legId, { inbound = [], outbound = [] }) => {
  const allSegments = [...outbound, ...inbound];
  const segment = allSegments.find(({ id }) => id === legId);

  if (!segment) {
    return [];
  }

  const { serviceIdentifier: selectedSegmentIdentifier } = segment;

  return allSegments.filter(({ serviceIdentifier }) => serviceIdentifier === selectedSegmentIdentifier);
};

const findSelectedPassengersSegments = (legId, selectedPassengers, { inbound, outbound }) => {
  const allSegments = findAllServiceSegments(legId, { inbound, outbound });

  const selectedPassengerIds = selectedPassengers.map(({ id }) => id);

  return allSegments.filter(({ seats }) => seats.find(({ passenger: { id } }) => selectedPassengerIds.includes(id)));
};

const findSelectedTrainSeats = (legId, { outbound = [], inbound = [] } = {}) => {
  const allSegments = findAllServiceSegments(legId, { inbound, outbound });

  return allSegments.flatMap(({ seats }) => seats);
};

const getCurrentInventoryClasses = (seats = []) =>
  [...new Set(seats.map(({ inventoryClass }) => inventoryClass))].filter((inventoryClass) =>
    SEAT_SELECTION_INVENTORY_CLASSES.includes(inventoryClass)
  );

const getUnavailableSeatsData = ({ seatingData = [], currentCoach, currentInventoryClasses, currentSeats, getCarriage }) => {
  const carriage = getCarriage(seatingData, currentCoach);
  const differentInventory = carriage.seats
    .filter(({ inventoryClass, blocked }) => !currentInventoryClasses.includes(inventoryClass) || blocked)
    ?.map(({ number, inventoryClass, blocked }) => ({ coach: carriage.carriageNumber, seat: number, inventoryClass, blocked }));
  const seatsAndCoaches = carriage.seats
    .filter(
      ({ inventoryClass, allocated, available, blocked }) =>
        currentInventoryClasses.includes(inventoryClass) && allocated && !available && !blocked
    )
    ?.map(({ number }) => ({ coach: carriage.carriageNumber, seat: number }));
  const otherBookingsSeatsAndCoaches = seatsAndCoaches.filter(
    ({ coach, seat }) => !currentSeats.some((p) => p.number === seat && p.coach === coach)
  );
  return { differentInventory, otherBookingsSeatsAndCoaches };
};

const groupSeatsByReference = ({ bookingReference, seats = [] }) =>
  seats
    .filter(({ reference }) => reference !== bookingReference)
    ?.reduce((acc, { reference, seat }) => {
      acc[reference] = acc[reference] || [];
      acc[reference].push(seat);
      return acc;
    }, {});

export {
  findSeatsToUpdate,
  pickSeats,
  findSelectedLeg,
  findAllServiceSegments,
  findSelectedTrainSeats,
  findSelectedPassengersSegments,
  getCurrentInventoryClasses,
  getUnavailableSeatsData,
  groupSeatsByReference
};
